// Generic music-theory helpers to transpose the white-key system to any key.
const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const MAJOR = [0, 2, 4, 5, 7, 9, 11]; // Ionian intervals
const ACC = { "-2": "bb", "-1": "b", "0": "", "1": "#", "2": "##" };

// Beginner-friendly keys (white-key roots + the common Bb)
export const KEYS = [
  { id: "C", letter: "C", off: 0, sig: "No sharps or flats" },
  { id: "G", letter: "G", off: 0, sig: "1 sharp: F#" },
  { id: "D", letter: "D", off: 0, sig: "2 sharps: F#, C#" },
  { id: "A", letter: "A", off: 0, sig: "3 sharps: F#, C#, G#" },
  { id: "E", letter: "E", off: 0, sig: "4 sharps: F#, C#, G#, D#" },
  { id: "F", letter: "F", off: 0, sig: "1 flat: Bb" },
  { id: "Bb", letter: "B", off: -1, sig: "2 flats: Bb, Eb" },
];

// Advanced keys (black-key roots). Eb is fairly common in pop; the rest are rarer.
export const ADVANCED_KEYS = [
  { id: "Bb", letter: "B", off: -1, sig: "2 flats: Bb, Eb", note: "Common in pop, soul & brass-driven songs." },
  { id: "Eb", letter: "E", off: -1, sig: "3 flats: Bb, Eb, Ab", note: "Common — lots of ballads and soul hits live here." },
  { id: "Ab", letter: "A", off: -1, sig: "4 flats: Bb, Eb, Ab, Db", note: "Rarer as a song key, but rich and warm." },
  { id: "Db", letter: "D", off: -1, sig: "5 flats: Bb, Eb, Ab, Db, Gb", note: "Uncommon — mostly for smooth, lush ballads." },
  { id: "B", letter: "B", off: 0, sig: "5 sharps: F#, C#, G#, D#, A#", note: "Uncommon as a root, but bright and ringing." },
  { id: "Gb", letter: "G", off: -1, sig: "6 flats: Bb, Eb, Ab, Db, Gb, Cb", note: "Rare — the far side of the circle (same as F#)." },
];

export const MODE_INFO = [
  { name: "Ionian", quality: "Major" },
  { name: "Dorian", quality: "minor" },
  { name: "Phrygian", quality: "minor" },
  { name: "Lydian", quality: "Major" },
  { name: "Mixolydian", quality: "Major" },
  { name: "Aeolian", quality: "minor" },
  { name: "Locrian", quality: "dim" },
];

const spell = (letter, pc) => {
  const basePc = LETTER_PC[letter];
  let d = (pc - basePc + 12) % 12;
  if (d > 6) d -= 12; // choose nearest accidental (-2..+2)
  return letter + ACC[String(d)];
};

// Returns the 7 diatonic notes of a major key: [{name, pc}]
export const scaleForKey = (key) => {
  const rootPc = (LETTER_PC[key.letter] + key.off + 12) % 12;
  const startIdx = LETTERS.indexOf(key.letter);
  return MAJOR.map((interval, i) => {
    const letter = LETTERS[(startIdx + i) % 7];
    const pc = (rootPc + interval) % 12;
    return { name: spell(letter, pc), pc };
  });
};

// Midis (48=C3 .. 72=C5) for a mode: start on the mode's root, walk up an octave
export const modeMidis = (diatonicPcs, rootPc) => {
  const set = new Set(diatonicPcs);
  const start = 48 + rootPc; // root in octave 3
  const out = [];
  for (let m = start; m <= start + 12; m++) {
    if (set.has(m % 12)) out.push(m);
  }
  return out;
};

// The 6 core triads (I ii iii IV V vi) transposed to a key
export const triadsForKey = (key) => {
  const scale = scaleForKey(key);
  const pcs = scale.map((s) => s.pc);
  return [0, 1, 2, 3, 4, 5].map((deg) => {
    const notes = [scale[deg], scale[(deg + 2) % 7], scale[(deg + 4) % 7]];
    const rootMidi = 48 + pcs[deg];
    const third = ((pcs[(deg + 2) % 7] - pcs[deg]) + 12) % 12;
    const quality = third === 4 ? "Major" : "minor";
    return { name: notes[0].name, quality, notes: notes.map((n) => n.name) };
  });
};
